import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { SignupInput, User } from "../types/user";
interface StoredAccount {
  user: User;
  password: string;
}
interface AuthContextValue {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  logout: () => void;
}
const ACCOUNTS_KEY = "rdk-accounts";
const SESSION_KEY = "rdk-session";
export const DEMO_ACCOUNT = {
  email: "demo@rd-kine.fr",
  password: "kine2026"
};
const demoStored: StoredAccount = {
  user: {
    firstName: "Camille",
    lastName: "Martin",
    email: DEMO_ACCOUNT.email,
    phone: "06 12 34 56 78"
  },
  password: DEMO_ACCOUNT.password
};
const AuthContext = createContext<AuthContextValue | null>(null);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(() => {
    const email = safeGet(SESSION_KEY);
    return email ? readAccounts()[email]?.user ?? null : null;
  });
  const login = useCallback(async (email: string, password: string) => {
    await wait(600);
    const account = readAccounts()[email.trim().toLowerCase()];
    if (!account) throw new Error("Aucun compte n'est associé à cette adresse e-mail.");
    if (account.password !== password) throw new Error("Mot de passe incorrect.");
    localStorage.setItem(SESSION_KEY, account.user.email);
    setUser(account.user);
  }, []);
  const signup = useCallback(async (input: SignupInput) => {
    await wait(700);
    const email = input.email.trim().toLowerCase();
    const accounts = readAccounts();
    if (accounts[email]) throw new Error("Un compte existe déjà avec cette adresse e-mail.");
    const newUser: User = {
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      email,
      phone: input.phone.trim()
    };
    accounts[email] = {
      user: newUser,
      password: input.password
    };
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
    localStorage.setItem(SESSION_KEY, email);
    setUser(newUser);
  }, []);
  const logout = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
  }, []);
  const value = useMemo(() => ({
    user,
    login,
    signup,
    logout
  }), [user, login, signup, logout]);
  return <AuthContext.Provider value={value} data-id="element-327" data-mp="ftxxushvpxyb">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
function readAccounts(): Record<string, StoredAccount> {
  let accounts: Record<string, StoredAccount> = {};
  try {
    const raw = safeGet(ACCOUNTS_KEY);
    accounts = raw ? JSON.parse(raw) as Record<string, StoredAccount> : {};
  } catch {
    accounts = {};
  }
  if (!accounts[DEMO_ACCOUNT.email]) accounts[DEMO_ACCOUNT.email] = demoStored;
  return accounts;
}
function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}